import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { AlertTriangle, AlertCircle, Info, Bell, TrendingDown, Users, BookOpen, ChevronRight } from 'lucide-react';

const API_URL = window.location.origin;

const alertaConfig = {
  alta: {
    icon: AlertTriangle,
    color: 'text-red-600',
    bg: 'bg-red-50 border-red-200',
    badge: 'bg-red-500'
  },
  media: {
    icon: AlertCircle,
    color: 'text-yellow-600',
    bg: 'bg-yellow-50 border-yellow-200',
    badge: 'bg-yellow-500'
  },
  baixa: {
    icon: Info,
    color: 'text-blue-600',
    bg: 'bg-blue-50 border-blue-200',
    badge: 'bg-blue-500'
  }
};

const tipoIcons = {
  aluno_critico: Users,
  aluno_queda: TrendingDown,
  disciplina_alerta: BookOpen,
  turma_recuperacao: Users
};

export function AlertasInteligentes({ year, filtros, onSelectAluno }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all');

  useEffect(() => {
    loadData();
  }, [year]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/central-notas/alertas?year=${year}&limit=50`);
      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error('Erro ao carregar alertas:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!data) return null;

  const { alertas, total } = data;

  // Filtrar alertas
  const filteredAlertas = selectedType === 'all' 
    ? alertas 
    : alertas.filter(a => a.tipo === selectedType);

  // Contagem por prioridade
  const countByPriority = {
    alta: alertas.filter(a => a.prioridade === 'alta').length,
    media: alertas.filter(a => a.prioridade === 'media').length,
    baixa: alertas.filter(a => a.prioridade === 'baixa').length
  };

  // Tipos únicos
  const tiposUnicos = [...new Set(alertas.map(a => a.tipo))];

  return (
    <div className="space-y-6" data-testid="alertas-inteligentes">
      {/* Header */}
      <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/20 rounded-xl">
                <Bell className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Central de Alertas</h2>
                <p className="text-purple-200">Monitoramento inteligente do desempenho acadêmico</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold">{total}</div>
              <div className="text-sm text-purple-200">alertas ativos</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resumo por Prioridade */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-red-200 bg-red-50 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setSelectedType('all')}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-red-700">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">Alta Prioridade</span>
                </div>
                <div className="text-3xl font-bold text-red-600 mt-2">{countByPriority.alta}</div>
              </div>
              <ChevronRight className="w-5 h-5 text-red-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-yellow-200 bg-yellow-50 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setSelectedType('all')}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-yellow-700">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">Média Prioridade</span>
                </div>
                <div className="text-3xl font-bold text-yellow-600 mt-2">{countByPriority.media}</div>
              </div>
              <ChevronRight className="w-5 h-5 text-yellow-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-blue-200 bg-blue-50 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setSelectedType('all')}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-blue-700">
                  <Info className="w-5 h-5" />
                  <span className="font-medium">Baixa Prioridade</span>
                </div>
                <div className="text-3xl font-bold text-blue-600 mt-2">{countByPriority.baixa}</div>
              </div>
              <ChevronRight className="w-5 h-5 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros por Tipo */}
      <Card className="border-purple-100">
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-slate-600">Filtrar por:</span>
            <Button
              variant={selectedType === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedType('all')}
              className={selectedType === 'all' ? 'bg-purple-600' : ''}
            >
              Todos ({total})
            </Button>
            {tiposUnicos.map((tipo) => {
              const count = alertas.filter(a => a.tipo === tipo).length;
              const labels = {
                aluno_critico: 'Alunos Críticos',
                aluno_queda: 'Queda de Desempenho',
                disciplina_alerta: 'Disciplinas',
                turma_recuperacao: 'Turmas'
              };
              return (
                <Button
                  key={tipo}
                  variant={selectedType === tipo ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedType(tipo)}
                  className={selectedType === tipo ? 'bg-purple-600' : ''}
                >
                  {labels[tipo] || tipo} ({count})
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Lista de Alertas */}
      <div className="space-y-3">
        {filteredAlertas.length === 0 ? (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6 pb-6 text-center">
              <div className="text-green-600 text-lg font-medium">
                Nenhum alerta encontrado para este filtro
              </div>
              <p className="text-green-500 text-sm mt-1">
                A situação acadêmica está sob controle!
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredAlertas.map((alerta, idx) => {
            const config = alertaConfig[alerta.prioridade] || alertaConfig.baixa;
            const Icon = tipoIcons[alerta.tipo] || AlertCircle;
            
            return (
              <Card 
                key={idx} 
                className={`${config.bg} cursor-pointer hover:shadow-md transition-all`}
                onClick={() => {
                  if (alerta.student_id && onSelectAluno) {
                    onSelectAluno(alerta.student_id);
                  }
                }}
              >
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${config.bg}`}>
                      <Icon className={`w-5 h-5 ${config.color}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={config.badge}>
                          {alerta.prioridade}
                        </Badge>
                        <span className="font-medium text-slate-700">{alerta.titulo}</span>
                      </div>
                      <p className="text-sm text-slate-600">{alerta.mensagem}</p>
                      {alerta.valor !== undefined && (
                        <div className="mt-2">
                          <span className="text-xs text-slate-500">Valor: </span>
                          <span className={`font-bold ${config.color}`}>{alerta.valor}</span>
                        </div>
                      )}
                    </div>
                    {alerta.student_id && (
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
